import { IExpedientKPIData } from 'bg-server/src/components/api/v1/cimne/types';
import { IExpedient, IExpedientPopulated, IExpedientSeguiments, IUserReceiver } from 'bg-server/src/components/api/v1/expedient/types';

import { ENV } from 'src/config/environments';
import { httpRequest } from 'src/helpers/http';
import { buildSearchRegex, normalizeString } from 'src/helpers/utils';
import { IObservacioBody, ISearchFilter } from 'src/interfaces';

class ExpedientService {
    private getApiEndpoint(): string {
        const selectedCity = localStorage.getItem('selectedCity') || ENV.apiEndpoints[0].key;
        const apiEndpoint = ENV.apiEndpoints.find(endpoint => endpoint.key === selectedCity);
        return apiEndpoint ? `${apiEndpoint.url}/expedient` : `${ENV.apiEndpoints[0].url}/expedient`;
    }

    // eslint-disable-next-line complexity
    private tranformSearchFilterExpedient(filter: ISearchFilter) {
        const searchField: any = {
            $and: [],
        };

        if (filter.endDate) {
            searchField.$and.push({
                created: {
                    $lte: filter.endDate,
                },
            });
        }
        if (filter.startDate) {
            searchField.$and.push({
                created: {
                    $gte: filter.startDate,
                },
            });
        }

        if (filter.id) {
            searchField.$and.push({
                $expr: {
                    $regexMatch: {
                        input: { $toString: '$_id' },
                        regex: filter.id,
                        options: 'i',
                    },
                },
            });
        }

        if (filter.dni) {
            const regex = buildSearchRegex(filter.dni);
            searchField.$and.push({ 'dadesIdentificacio.dni': { $regex: regex, $options: 'i' } });
        }

        if (filter.nom) {
            const regex = buildSearchRegex(filter.nom);
            searchField.$and.push({ 'dadesIdentificacio.nom': { $regex: regex, $options: 'i' } });
        }

        if (filter.cognom) {
            const regex = buildSearchRegex(filter.cognom);
            searchField.$and.push({ 'dadesIdentificacio.cognoms': { $regex: regex, $options: 'i' } });
        }

        if (filter.address) {
            searchField.$and.push({ $text: { $search: normalizeString(filter.address) } });
        }

        if (filter.email) {
            const regex = buildSearchRegex(filter.email);
            searchField.$and.push({ 'dadesIdentificacio.email': { $regex: regex, $options: 'i' } });
        }

        if (filter.telefon) {
            const regex = buildSearchRegex(filter.telefon);
            searchField.$and.push({
                $or: [
                    { 'dadesIdentificacio.telefon1': { $regex: regex, $options: 'i' } },
                    { 'dadesIdentificacio.telefon2': { $regex: regex, $options: 'i' } },
                ],
            });
        }

        if (filter.cups) {
            const regex = buildSearchRegex(filter.cups);
            searchField.$and.push({
                $or: [
                    { 'preForms.CUPSiadrecasubministramentelectricitat.CUPSCodiUnicdelPuntdeSubministrament': { $regex: regex, $options: 'i' } },
                    { 'preForms.CUPSiadrecasubministramentgas.CUPSCodiUnicdelPuntdeSubministrament': { $regex: regex, $options: 'i' } },
                ],
            });
        }

        if (filter.professionals && filter.professionals.length) {
            searchField.$and.push({ 'dadesIdentificacio.Professionalqueportalexpedient': { $in: filter.professionals } });
        }

        if (filter.districts && filter.districts.length > 1) {
            searchField.$and.push({
                'dadesIdentificacio.districte': {
                    $in: filter.districts,
                },
            });
        }

        return searchField;
    }

    // eslint-disable-next-line complexity
    async searchExpedientsByFilter(filter: ISearchFilter, page: number, pageSize = 20, isCustomFilter = false): Promise<{ count:number, data:IExpedient[] }> {
        let sFilter: any = {};
        let hasSearchFilter = false;
        if (isCustomFilter) {
            sFilter = filter;
            hasSearchFilter = true;
        } else {
            sFilter = this.tranformSearchFilterExpedient(filter);

            hasSearchFilter = Boolean(
                filter.searchString
                || filter.dni
                || filter.id
                || filter.address
                || filter.cognom
                || filter.nom
                || filter.email
                || filter.telefon
                || filter.cups
                || filter.startDate
                || filter.endDate
                || (filter.professionals && filter.professionals.length > 0)
                || (filter.districts && filter.districts.length > 0),
            );
        }

        const filterParams = hasSearchFilter
            ? `search=${encodeURIComponent(JSON.stringify(sFilter))}&`
            : '';

        const sortParams = filter.sortOrder ? `sort=${filter.sortOrder}&` : '';
        const pageParams = page ? `page=${page}&` : '';
        const pageSizeParams = pageSize ? `pageitems=${pageSize}` : '';

        const response = await httpRequest(`${this.getApiEndpoint()}?${sortParams}${filterParams}${pageParams}${pageSizeParams}`, 'GET');
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    async getExpedientById(id: string): Promise<IExpedient> {
        const response = await httpRequest(`${this.getApiEndpoint()}/${id}`, 'GET');
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    async downloadInformePDF(id: string): Promise<Blob> {
        const response = await httpRequest(`${this.getApiEndpoint()}/${id}/download.pdf`, 'GET');
        if (!response.ok) {
            const json = await response.json();
            if (json.error !== undefined) {
                await Promise.reject(json.message);
            } else {
                await Promise.reject(new Error(`Failed to download PDF: ${response.statusText}`));
            }
        }
        const blob = await response.blob();
        return blob;
    }

    async addExpedient(data: IExpedient): Promise<IExpedient> {
        const response = await httpRequest(this.getApiEndpoint(), 'POST', data);
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    async updateExpedient(expedientId: string, data: IExpedient): Promise<IExpedient> {
        const response = await httpRequest(`${this.getApiEndpoint()}/${expedientId}`, 'PUT', data);
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    async deleteExpedient(expedientId: string): Promise<IExpedient> {
        const response = await httpRequest(`${this.getApiEndpoint()}/${expedientId}`, 'DELETE');
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    async addObservacio(expedientId: string, data: IObservacioBody): Promise<IExpedient> {
        const response = await httpRequest(`${this.getApiEndpoint()}/${expedientId}/observacio`, 'POST', data);
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    async updateObservacio(expedientId: string, observacioId: string, data: IObservacioBody): Promise<IExpedient> {
        const response = await httpRequest(`${this.getApiEndpoint()}/${expedientId}/observacio/${observacioId}`, 'PUT', data);
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    async deleteObservacio(expedientId: string, observacioId: string): Promise<IExpedient> {
        const response = await httpRequest(`${this.getApiEndpoint()}/${expedientId}/observacio/${observacioId}`, 'DELETE');
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    async toggleApteAtramitar(expedientId: string): Promise<IExpedientPopulated> {
        const response = await httpRequest(`${this.getApiEndpoint()}/${expedientId}/toggle-apte-atramitar`, 'PUT');
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    async registerSyncUser(expedientId: string, data: any): Promise<IExpedient> {
        const response = await httpRequest(`${this.getApiEndpoint()}/${expedientId}/user`, 'POST', data);
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    async getUsersByCoordinates(lng: number, lat: number): Promise<IUserReceiver[]> {
        const response = await httpRequest(`${this.getApiEndpoint()}/users-by-coordinates?xcoord=${lng}&ycoord=${lat}`, 'GET');
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    async getUsersByAge(age: number): Promise<IUserReceiver[]> {
        const response = await httpRequest(`${this.getApiEndpoint()}/users-by-age?age=${age}`, 'GET');
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    async getExpedientWithSegiments(idExpedient: string): Promise<IExpedientSeguiments> {
        const response = await httpRequest(`${this.getApiEndpoint()}/${idExpedient}/seguiment`, 'GET');
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    async completeExpedientSegiment(idExpedient: string, idSeguiment: string): Promise<IExpedientSeguiments> {
        const response = await httpRequest(`${this.getApiEndpoint()}/${idExpedient}/seguiment/${idSeguiment}/complete`, 'PUT');
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    async addExpedientSeguiment(idExpedient: string, data: any): Promise<IExpedientSeguiments> {
        const response = await httpRequest(`${this.getApiEndpoint()}/${idExpedient}/seguiment`, 'POST', data);
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    async getUsersByNumPersonesUnitatFalimiar(numPersonesUnitatFalimiar: number): Promise<IUserReceiver[]> {
        const response = await httpRequest(`${this.getApiEndpoint()}/users-by-num-unitat-familiar?npersones=${numPersonesUnitatFalimiar}`, 'GET');
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    async approveExpedientSolicitud(idExpedient: string, solicitudType: string, isApproved: boolean): Promise<IExpedientSeguiments> {
        const data = {
            type: solicitudType,
            approved: isApproved,
        };
        const response = await httpRequest(`${this.getApiEndpoint()}/${idExpedient}/approve`, 'POST', data);
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    async removeProfessionalAsignat(idExpedient: string): Promise<IExpedientPopulated> {
        const response = await httpRequest(`${this.getApiEndpoint()}/${idExpedient}/remove-professional`, 'PUT');
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    async unlinkUser(idExpedient: string): Promise<IExpedientPopulated> {
        const response = await httpRequest(`${this.getApiEndpoint()}/${idExpedient}/unlink-user`, 'PUT');
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    async getExpedientKPI(idExpedient: string): Promise<IExpedientKPIData> {
        const response = await httpRequest(`${this.getApiEndpoint()}/${idExpedient}/kpi`, 'GET');
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }
}

export const expedientService = new ExpedientService();
